import { Chip, cn, LogoPay } from "@au-schein-de/au-ui-library";
import type { KeyboardEvent, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { PharmacyPickerSortMenu } from "~/features/products/pharmacy-picker/pharmacy-picker-sort-menu.component";
import type { PharmacyPickerSortContext } from "~/features/products/pharmacy-picker/pharmacy-picker-sort-preference";
import { PharmacyPickerTopFilterBadge } from "~/features/products/pharmacy-picker/pharmacy-picker-top-filter-badge.component";

export type PharmacyPickerFilterCardsProps = {
	topSelected: boolean;
	paySelected: boolean;
	/**
	 * Omitted on surfaces with no Top/non-Top split (courier, Abholung), which
	 * is how the Top chip stays out of the row there.
	 */
	onTopChange?: (selected: boolean) => void;
	onPayChange: (selected: boolean) => void;
	sortContext: PharmacyPickerSortContext;
	/**
	 * Figma 4249:80482: on desktop the radius selector sits inside this
	 * filter row (before Top/Pay), not next to the picker's own trigger.
	 * Hidden below `md` — mobile keeps its own radius entry point on the
	 * fullscreen map.
	 */
	radiusControl?: ReactNode;
	/**
	 * A `variant="sheet"` RadiusSelect for callers with no separate mobile
	 * radius entry point (PDP has no fullscreen-map companion). Hidden at
	 * `md` and up, where `radiusControl` takes over.
	 */
	mobileRadiusControl?: ReactNode;
	/**
	 * Mobile-only delivery-method switcher (PharmacyPickerDeliveryMethodChip),
	 * first in the row (Figma 602). No desktop counterpart is needed here —
	 * the header's own switcher is always visible on desktop.
	 */
	deliveryMethodControl?: ReactNode;
	/**
	 * Narrows the list to pharmacies express courier can reach right now.
	 * Rendered only when the owner passes `onExpressChange`: the verdict needs
	 * an address, so a courier surface without one leaves the chip out rather
	 * than showing a filter that cannot answer. Sits directly after the
	 * delivery-method chip, which makes it first in the row on desktop, where
	 * that chip is hidden.
	 */
	expressSelected?: boolean;
	onExpressChange?: (selected: boolean) => void;
	/**
	 * Merged onto the scroll container. Callers sitting inside a padded
	 * column use it to bleed the row to the device edge (Figma 4643:88788).
	 */
	className?: string;
};

const filterChipClassName =
	"h-9 shrink-0 cursor-pointer gap-2 rounded-xl bg-gray-100 p-3 text-body-sm font-medium text-gray-700 stroke-none hover:stroke-none focus:ring-0 focus:ring-offset-0 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ansay-700";

function activateOnEnterOrSpace(
	event: KeyboardEvent<HTMLElement>,
	action: () => void,
): void {
	if (event.key === "Enter" || event.key === " ") {
		event.preventDefault();
		action();
	}
}

function ToggleFilterCard({
	label,
	accessibleName,
	selected,
	testId,
	icon,
	onChange,
}: {
	label?: string;
	accessibleName: string;
	selected: boolean;
	testId: string;
	icon?: ReactNode;
	onChange: (selected: boolean) => void;
}) {
	return (
		<Chip
			variant="filter"
			className={cn(
				filterChipClassName,
				selected &&
					"border-0 bg-ansay-700 font-bold text-white hover:bg-ansay-700 hover:text-white",
			)}
			data-testid={testId}
			// biome-ignore lint/a11y/useSemanticElements: Chip is not a native button
			role="button"
			tabIndex={0}
			aria-label={accessibleName}
			aria-pressed={selected}
			onClick={() => onChange(!selected)}
			onKeyDown={(event) => {
				activateOnEnterOrSpace(event, () => onChange(!selected));
			}}
		>
			{icon ?? null}
			{label}
		</Chip>
	);
}

export function PharmacyPickerFilterCards({
	topSelected,
	paySelected,
	onTopChange,
	onPayChange,
	sortContext,
	radiusControl,
	mobileRadiusControl,
	deliveryMethodControl,
	expressSelected = false,
	onExpressChange,
	className,
}: PharmacyPickerFilterCardsProps) {
	const { t } = useTranslation("product");
	const topLabel = t("vendor.picker.filter.top");
	const payLabel = t("vendor.picker.filter.pay");
	const expressLabel = t("vendor.picker.filter.express");
	// One scroll container for every pill. Radius and sort used to sit outside
	// it, so they stayed pinned on narrow viewports while only Top/Pay scrolled.
	return (
		<div
			className={cn(
				"flex min-w-0 gap-2 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
				className,
			)}
			data-testid="pharmacy-picker-filter-cards"
		>
			{deliveryMethodControl ?? null}
			{onExpressChange ? (
				<ToggleFilterCard
					label={expressLabel}
					accessibleName={expressLabel}
					selected={expressSelected}
					testId="pharmacy-picker-filter-express"
					onChange={onExpressChange}
				/>
			) : null}
			{radiusControl ? (
				<div className="hidden shrink-0 md:flex">{radiusControl}</div>
			) : null}
			{mobileRadiusControl ? (
				<div className="shrink-0 md:hidden">{mobileRadiusControl}</div>
			) : null}
			{onTopChange ? (
				<ToggleFilterCard
					label={topLabel}
					accessibleName={topLabel}
					selected={topSelected}
					testId="pharmacy-picker-filter-top"
					icon={
						<PharmacyPickerTopFilterBadge
							tone={topSelected ? "inverse" : "brand"}
							data-testid="pharmacy-picker-filter-top-badge"
						/>
					}
					onChange={onTopChange}
				/>
			) : null}
			<ToggleFilterCard
				accessibleName={payLabel}
				selected={paySelected}
				testId="pharmacy-picker-filter-pay"
				icon={
					<LogoPay
						title="DrAnsay Pay"
						variant={paySelected ? "light" : "dark"}
						className={cn(
							"h-5 w-9 shrink-0 stroke-none",
							paySelected && "[&_path]:fill-white",
						)}
						data-testid="pharmacy-picker-filter-pay-logo"
					/>
				}
				onChange={onPayChange}
			/>
			<PharmacyPickerSortMenu
				chipClassName={filterChipClassName}
				sortContext={sortContext}
			/>
		</div>
	);
}
